import { Box, Flex } from "@chakra-ui/react";
import type { SourcingEventDto } from "../../../autogen/bff-api";
import { useCurrentMoment } from "../../sourcing-events/portal/utils/useCurrentMoment";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { deriveTimelinePhase, phasesFor, type TimelinePhaseKey } from "./deriveTimelinePhase";
import { publishedRounds } from "./latestPublishedRound";
import { latestStage as lastStage, negotiationStages } from "./negotiationStages";
import { NegotiationRoundsBadge } from "./NegotiationRoundsBadge";
import { PhaseStep } from "./PhaseStep";

// Once a competition negotiates, the tilbudsfrist and the evaluation are both inside the
// negotiation — they stay two steps, joined by the round count rather than split by it.
const NEGOTIATION_GROUP: readonly TimelinePhaseKey[] = ["bidDeadline", "evaluationStart"];

interface Props {
  event: SourcingEventDto;
}

export const EventPhaseTimeline = ({ event }: Props) => {
  const { rounds } = useRoundSelection();

  // Ticks ~every minute so the stepper crosses the bid-deadline / standstill boundary on its own
  // while the page stays open, without a manual refresh.
  const now = useCurrentMoment().valueOf();
  const phase = deriveTimelinePhase(event, now);
  if (!phase.visible) return null;

  const phases = phasesFor(event);
  const completed = new Set(phase.completedKeys);
  // The pager selection never reaches here: the timeline speaks for where the competition stands.
  const sentRounds = publishedRounds(rounds);
  const stages = negotiationStages(event, sentRounds);
  const latestStage = lastStage(stages);
  const groupsNegotiation =
    sentRounds.length > 0 && NEGOTIATION_GROUP.every((key) => phases.includes(key));

  const stepFor = (key: TimelinePhaseKey) => (
    <PhaseStep
      key={key}
      event={event}
      phaseKey={key}
      stepNumber={phases.indexOf(key) + 1}
      isActive={phase.activeKey === key}
      isDone={completed.has(key)}
      latestStage={latestStage}
    />
  );

  return (
    <Box
      mt="6"
      bg="smBackground"
      rounded="xl"
      border="1px solid"
      borderColor="smBorder"
      p={{ base: "4", md: "6" }}
      opacity={phase.idle ? 0.5 : 1}
    >
      <Box position="relative">
        {/* Connecting rail */}
        <Box
          position="absolute"
          bg="smBorder"
          zIndex={0}
          top="13px"
          left={{ base: "13px", md: "3%" }}
          right={{ base: "auto", md: "3%" }}
          bottom={{ base: "13px", md: "auto" }}
          height={{ base: "auto", md: "2px" }}
          width={{ base: "2px", md: "auto" }}
        />

        <Flex
          direction={{ base: "column", md: "row" }}
          position="relative"
          zIndex={1}
          gap={{ base: 4, md: 2 }}
          align={{ md: "flex-start" }}
        >
          {phases.map((key) => {
            if (groupsNegotiation && key === "evaluationStart") return null;
            if (groupsNegotiation && key === "bidDeadline") {
              return (
                <Flex
                  key="negotiation-group"
                  direction="column"
                  flex={{ md: "2 1 0" }}
                  minW="0"
                >
                  <Flex
                    direction={{ base: "column", md: "row" }}
                    align={{ base: "stretch", md: "flex-start" }}
                    gap={{ base: 4, md: 2 }}
                  >
                    {NEGOTIATION_GROUP.map(stepFor)}
                  </Flex>
                  <NegotiationRoundsBadge stages={stages} roundCount={sentRounds.length} />
                </Flex>
              );
            }
            return stepFor(key);
          })}
        </Flex>
      </Box>
    </Box>
  );
};
